import {Button, ButtonGroup, Stack, Typography} from '@mui/material';
import {useState, useContext, useEffect} from 'react';
import {consistentSpacing} from './variables';
import APIServiceFormContext from '../../context/APIServiceFormContext';

/**
 * A component representing a boolean field
 * @param {Object} props - Component props
 * @param {string} props.title
 * @param {ReactNode} props.children
 * @param {number|string|object} props.width - Width of the component(accepts a MUI width prop)
 *
 * @returns {React.Component}
 */
const BooleanField = ({title, children, width}) => (
  <Stack
    sx={{
      width: width === undefined ? 'fit-content' : width,
      mx: 'auto',
      maxWidth: width === undefined ? 'fit-content' : '80vw',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
    spacing={consistentSpacing}
  >
    <Typography variant="subtitle0" my={{xs: 1, sm: 3}}>
      {title}
    </Typography>
    {children}
  </Stack>
);

/**
 * A component representing a boolean select button
 * @param {Object} props
 * @param {string} props.text
 * @param {boolean} props.selected
 * @param {function} props.setSelected
 *
 * @returns {React.Component}
 */
const BooleanSelectButton = ({text, selected, setSelected}) => (
  <Button
    color={selected ? 'primary' : 'grayscale'}
    sx={{
      color: selected ? 'white' : 'black',
      borderRadius: 5,
      fontWeight: 500,
      fontSize: 14,
    }}
    size="small"
    variant="contained"
    onClick={setSelected}
  >
    {text}
  </Button>
);

/**
 * BooleanSelectButtonGroup
 * Component that renders a question with a select component with only 2 options to choose, all of this while being responsive and most importantly: state is not being managed directly in this component, it should be done from the parent component. See this directory's README.md for more details.
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Main text that will appear on the component
 * @param {number|string|object} props.width - Width of the component(accepts a MUI width prop)
 * @param {Array.<{
 *  text: string // Text shown
 *  value: * // Value that will be saved
 * }>} props.choices - Array representing what data is needed from the choices this component will have
 *
 * @returns {React.Component} Rendered Component
 */
export const BooleanSelectButtonGroup = ({title, choices, width}) => {
  const {setValidationFunc, setSendDataArgs, savedValue} = useContext(APIServiceFormContext);
  const [selected, setSelected] = useState(savedValue === undefined ? null : savedValue);

  const componentValidationWrapper = () => {
    function componentValidation() {
      return selected === null ? 'Please select an option.' : '';
    }

    return componentValidation;
  };

  useEffect(() => {
    setSelected(savedValue === undefined ? null : savedValue);
  }, [savedValue]);
  useEffect(() => {
    setValidationFunc(componentValidationWrapper);
    setSendDataArgs({data: selected});
  }, [selected]);
  useEffect(() => {
    setSelected(savedValue === undefined ? null : savedValue);
  }, [title]);

  return (
    <BooleanField title={title} width={width}>
      <ButtonGroup
        fullWidth
        variant={selected != null ? 'text' : 'contained'}
        sx={{
          borderColor: 'black',
          width: 1,
          pr: width ? 1 : 0,
          boxSizing: 'border-box',
          marginTop: 1,
          '& .MuiButtonGroup-grouped:not(:last-of-type)': {
            borderColor: '#D9D9D9',
          },
        }}
      >
        {choices.map((choice, index) => (
          <BooleanSelectButton
            key={index}
            text={choice.text}
            selected={selected == choice.value}
            setSelected={() => {
              setSelected(choice.value);
            }}
          />
        ))}
      </ButtonGroup>
    </BooleanField>
  );
};
